<template>
  <div class="overview-page">
    <!-- Show content only if user is logged in -->
    <div v-if="currentUser">
      <!-- Top Toolbar -->
      <div class="top-toolbar">
        <div class="toolbar-left">
          <h1 class="page-title">Admin Overview</h1>
        </div>
        <div class="toolbar-right">
          <div class="user-info">
            <span class="user-icon">👤</span>
            <span class="user-details">
              <span class="user-name">{{ currentUser.name || 'Admin User' }}</span>
            </span>
          </div>
          <div class="environment-info">
            <span class="env-icon">🌐</span>
            <span class="env-text">{{ getEnvironment() }}</span>
          </div>
        </div>
      </div>

      <!-- Quick Actions -->
      <div class="quick-actions">
        <h2 class="section-title">Quick Actions</h2>
        <div class="actions-grid">
          <button class="quick-action-btn" @click="navigateTo('/dealerships/new')" v-if="hasRole(['supusr'])">
            <span class="action-icon">➕</span>
            Add New Dealership
          </button>
          <button class="quick-action-btn" @click="navigateTo('/cms')">
            <span class="action-icon">📝</span>
            Edit Content
          </button>
          <button class="quick-action-btn" @click="navigateTo('/automapper')" v-if="hasRole(['supusr'])">
            <span class="action-icon">🚗</span>
            Automapper
          </button>
          <button class="quick-action-btn" @click="navigateTo('/targetsites')" v-if="hasRole(['supusr'])">
            <span class="action-icon">🌐</span>
            Target Sites
          </button>
        </div>
      </div>

      <!-- Analytics Dashboard -->
      <div class="analytics-section">
        <div class="section-header">
          <h2 class="section-title">Business Analytics</h2>
          <button class="refresh-btn" @click="refreshData()">
            <span class="action-icon">🔄</span>
            Refresh Data
          </button>
        </div>
        
        <div class="analytics-grid">
          <!-- Dealerships Count -->
          <div class="analytics-card">
            <div class="card-header">
              <div class="card-icon dealership">👑</div>
              <div class="card-info">
                <h3 class="card-title">Dealerships</h3>
                <p class="card-subtitle">Total number of dealerships</p>
              </div>
            </div>
            <div class="card-metric">
              <span class="metric-value" v-if="!loading">{{ analytics.dealerships }}</span>
              <span class="metric-loading" v-else>...</span>
            </div>
            <div class="card-actions">
              <button class="action-btn outline" @click="navigateTo('/dealerships')" v-if="hasRole(['supusr'])">
                View All
              </button>
            </div>
          </div>

          <!-- Rooftops Count -->
          <div class="analytics-card">
            <div class="card-header">
              <div class="card-icon showroom">📍</div>
              <div class="card-info">
                <h3 class="card-title">Rooftops</h3>
                <p class="card-subtitle">Total number of showrooms</p>
              </div>
            </div>
            <div class="card-metric">
              <span class="metric-value" v-if="!loading">{{ analytics.rooftops }}</span>
              <span class="metric-loading" v-else>...</span>
            </div>
            <div class="card-actions">
              <button class="action-btn outline" @click="navigateTo('/dealerships')" v-if="hasRole(['supusr'])">
                View All
              </button>
            </div>
          </div>

          <!-- Active Bots Count -->
          <div class="analytics-card">
            <div class="card-header">
              <div class="card-icon bots">🤖</div>
              <div class="card-info">
                <h3 class="card-title">Active Bots</h3>
                <p class="card-subtitle">Total number of active bots</p>
              </div>
            </div>
            <div class="card-metric">
              <span class="metric-value" v-if="!loading">{{ analytics.activeBots }}</span>
              <span class="metric-loading" v-else>...</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Show login prompt if user is not logged in -->
    <div v-else class="login-required">
      <div class="login-card">
        <h2 class="login-title">Authentication Required</h2>
        <p class="login-message">Please log in to access the admin dashboard.</p>
        <button class="login-btn" @click="showLogin">
          <span class="login-icon">🔐</span>
          Log In
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'OverviewView',
  data() {
    return {
      currentUser: null,
      loading: true,
      analytics: {
        dealerships: 0,
        rooftops: 0,
        activeBots: 0
      }
    }
  },
  async created() {
    try {
      this.currentUser = await window.seezSdk.getCurrentUser()
      if (this.currentUser) {
        await this.fetchAnalytics()
      } else {
        // Automatically show login if user is not authenticated
        this.showLogin()
      }
    } catch (error) {
      console.error('Error getting current user:', error)
      // Show login on error as well
      this.showLogin()
    }
  },
  mounted() {
    // Subscribe to user changes
    if (window.seezSdk?.userSubscribe) {
      window.seezSdk.userSubscribe(user => {
        this.currentUser = user
        // Fetch analytics when user logs in
        if (user && this.hasRole(['supusr'])) {
          this.fetchAnalytics()
        }
      })
    }
  },
  methods: {
    async fetchAnalytics() {
      if (!this.hasRole(['supusr'])) {
        this.loading = false
        return
      }

      try {
        this.loading = true
        
        // Check if analytics data exists in localStorage
        const cachedAnalytics = this.getCachedAnalytics()
        if (cachedAnalytics) {
          this.analytics = cachedAnalytics
          this.loading = false
          return
        }

        // Query to get dealerships count using pagination
        const QUERY_DEALERSHIPS = `
          query getDealershipsCount {
            businessUnits(type: [dealership, franchise]) {
              pageInfo {
                total
              }
            }
          }
        `

        // Query to get showrooms count using pagination
        const QUERY_SHOWROOMS = `
          query getShowroomsCount {
            businessUnits(type: [showroom]) {
              pageInfo {
                total
              }
            }
          }
        `

        // Query to get all bots
        const QUERY_BOTS = `
          query getAllBots {
            bots {
              id
              disabled
            }
          }
        `

        // Execute all queries in parallel
        const [dealershipsResponse, showroomsResponse, botsResponse] = await Promise.all([
          window.seezSdk.queryApi(QUERY_DEALERSHIPS),
          window.seezSdk.queryApi(QUERY_SHOWROOMS),
          window.seezSdk.queryApi(QUERY_BOTS)
        ])

        // Get counts from pagination info
        this.analytics.dealerships = dealershipsResponse.businessUnits?.pageInfo?.total || 0
        this.analytics.rooftops = showroomsResponse.businessUnits?.pageInfo?.total || 0
        
        // Count active bots (filter out disabled ones)
        this.analytics.activeBots = (botsResponse.bots || []).filter(bot => 
          !bot.disabled
        ).length

        // Cache the analytics data
        this.cacheAnalytics(this.analytics)

      } catch (error) {
        console.error('Error fetching analytics:', error)
      } finally {
        this.loading = false
      }
    },

    getCachedAnalytics() {
      try {
        const cachedData = localStorage.getItem('seez-admin-analytics')
        if (cachedData) {
          const parsed = JSON.parse(cachedData)
          // Check if cache is still valid (you can add timestamp validation here if needed)
          return parsed
        }
      } catch (error) {
        console.error('Error reading cached analytics:', error)
      }
      return null
    },

    cacheAnalytics(analyticsData) {
      try {
        localStorage.setItem('seez-admin-analytics', JSON.stringify(analyticsData))
      } catch (error) {
        console.error('Error caching analytics:', error)
      }
    },

    clearAnalyticsCache() {
      try {
        localStorage.removeItem('seez-admin-analytics')
      } catch (error) {
        console.error('Error clearing analytics cache:', error)
      }
    },
    
    navigateTo(path) {
      this.$router.push(path)
    },
    
    hasRole(roles) {
      if (!this.currentUser || !this.currentUser.roles) return false
      return this.currentUser.roles.some(r => roles.includes(r.rolecode))
    },
    
    getUserRoles(user) {
      if (!user || !user.roles) return 'Guest'
      return user.roles.map(r => r.rolecode).join(', ')
    },
    
    getEnvironment() {
      // You can customize this based on your environment detection logic
      return import.meta.env.MODE || 'production'
    },
    
    async refreshData() {
      // Clear the cache and fetch fresh data
      this.clearAnalyticsCache()
      await this.fetchAnalytics()
    },

    showLogin() {
      if (window.seezSdk && window.seezSdk.showLogin) {
        window.seezSdk.showLogin()
      }
    }
  }
}
</script>

<style scoped>
.overview-page {
  max-width: 100%;
  margin: 0 auto;
  padding: 0;
}

/* Top Toolbar Styles */
.top-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  margin-bottom: 2rem;
}

.toolbar-left .page-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--color-text);
  margin: 0;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: 6px;
}

.user-icon {
  font-size: 1.25rem;
}

.user-details {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.user-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text);
}

.user-role {
  font-size: 0.75rem;
  color: var(--color-text-light);
}

.environment-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--color-primary);
  color: white;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
}

.env-icon {
  font-size: 1rem;
}

/* Analytics Section */
.analytics-section {
  margin-bottom: 3rem;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-text);
  margin: 0 0 1rem 0;
}

.refresh-btn {
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  color: var(--color-text);
  font-size: 0.875rem;
}

.refresh-btn:hover {
  background: var(--color-primary);
  color: white;
  border-color: var(--color-primary);
}

.analytics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
}

.analytics-card {
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
}

.analytics-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.card-header {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.card-icon {
  font-size: 1.5rem;
  width: 3rem;
  height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  color: white;
  flex-shrink: 0;
}

.card-icon.dealership {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.card-icon.showroom {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

.card-icon.bots {
  background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
}

.card-info {
  flex: 1;
  min-width: 0;
}

.card-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--color-text);
  margin: 0 0 0.25rem 0;
}

.card-subtitle {
  font-size: 0.875rem;
  color: var(--color-text-light);
  margin: 0;
  line-height: 1.4;
}

.card-metric {
  margin-bottom: 1.5rem;
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
}

.metric-value {
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--color-text);
}

.metric-loading {
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--color-text-light);
}



.card-actions {
  display: flex;
  gap: 0.75rem;
}

.action-btn {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
}

.action-btn.outline {
  background: transparent;
  border: 1px solid var(--color-border);
  color: var(--color-text);
}

.action-btn.outline:hover {
  background: var(--color-primary);
  color: white;
  border-color: var(--color-primary);
}

/* Quick Actions */
.quick-actions {
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  margin-bottom: 2rem;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.actions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.quick-action-btn {
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 500;
  color: var(--color-text);
}

.quick-action-btn:hover {
  background: var(--color-primary);
  color: white;
  border-color: var(--color-primary);
}

.action-icon {
  font-size: 1.25rem;
}

/* Login Required Styles */
.login-required {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 2rem;
}

.login-card {
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  padding: 3rem 2rem;
  text-align: center;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  max-width: 400px;
  width: 100%;
}

.login-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-text);
  margin: 0 0 1rem 0;
}

.login-message {
  color: var(--color-text-light);
  margin: 0 0 2rem 0;
  line-height: 1.5;
}

.login-btn {
  background: var(--color-primary);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 2rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 auto;
}

.login-btn:hover {
  background: var(--color-primary-dark, #2563eb);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.login-icon {
  font-size: 1.125rem;
}

/* Responsive design */
@media (max-width: 768px) {
  .top-toolbar {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  
  .toolbar-right {
    justify-content: space-between;
  }
  
  .section-header {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  
  .analytics-grid {
    grid-template-columns: 1fr;
  }
  
  .actions-grid {
    grid-template-columns: 1fr;
  }
  
  .user-details {
    display: none;
  }
  
  .user-info {
    justify-content: center;
    min-width: auto;
  }
}

@media (max-width: 480px) {
  .toolbar-right {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .metric-value, .metric-loading {
    font-size: 2rem;
  }
  
  .card-header {
    flex-direction: column;
    gap: 0.75rem;
    text-align: center;
  }
}
</style>
